import { useEffect } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { trackEvent, trackToolUseSpent } from "@/lib/analytics";
import { onToolUseSpent } from "@/lib/usageGate";
import { getToolById } from "@/lib/tools-data";
import { applyInternalParam, setInternalTraffic } from "@/lib/internalTraffic";

const SESSION_FLAG = "ga4_login_tracked";

/**
 * Mount exactly once at the app root (see App.tsx). Fires a GA4 "login"
 * event the first time a session becomes authenticated, guarded by
 * sessionStorage so it doesn't refire on every page navigation while
 * already signed in, and doesn't double-fire just because multiple other
 * components also call useAuth() elsewhere in the tree.
 *
 * Honest limitation: this can't currently distinguish a brand-new signup
 * from a returning user logging back in without a small server-side flag
 * on the auth response -- it tracks "successfully authenticated" as a
 * single event either way. Good enough to see how many people complete
 * sign-in at all; a true sign_up vs. login split would need a follow-up.
 */
export function AuthAnalyticsTracker() {
  const { isAuthenticated, loading, user } = useAuth();

  // tool_completed for every tool, from one place: whenever a tool visit spends its trial use (download, copy,
  // print, save, a Check or Run button). Tools that report their own completion are matched, not doubled.
  useEffect(() => {
    onToolUseSpent((toolId) => {
      const category = getToolById(toolId)?.category;
      if (category) trackToolUseSpent(toolId, category);
    });
    return () => onToolUseSpent(null);
  }, []);
  // ?fdp_internal=1 / 0 flags or unflags this device as the owner's own (see lib/internalTraffic.ts).
  useEffect(() => { try { applyInternalParam(window.location.search); } catch { /* ignore */ } }, []);
  // Admin accounts are never customers: flag the device so GA4 can exclude it.
  useEffect(() => { if (user?.role === "admin") setInternalTraffic(true); }, [user?.role]);

  useEffect(() => {
    if (loading || !isAuthenticated) return;
    if (sessionStorage.getItem(SESSION_FLAG)) return;

    trackEvent("login", { method: "freedailypro" });
    sessionStorage.setItem(SESSION_FLAG, "1");
  }, [isAuthenticated, loading]);

  return null;
}
